import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../../../../app/store';
import { getUserGenericConfig } from '../../../../app/features/sessions/sessionConfig/sessionUserConfigurations';
import { GeneralUserConfigForm } from '../../../../app/components/forms/userConfig/GeneralUserConfigForm';
import { UserConfigSideBar } from '../../../../app/components/sideBar/UserConfigSideBar';
import { PrivacityUserConfigForm } from '../../../../app/components/forms/userConfig/PrivacityUserConfigForm';
import { AccessibilityUserConfigForm } from '../../../../app/components/forms/userConfig/AccessibilityUserConfigForm';
import BillingInfoForm from '../../../../app/components/configOptions/BillingInfoForm';
import * as REQUEST_REQUIREMENTS from '../../../../app/api/requestRequirements';
import { Box, CircularProgress, Container, Typography } from '@mui/material';

const BG_MAIN    = '#15151a';
const BRAND_RED  = '#c0392b';
const TEXT_PRIMARY = '#f4f4f6';

export function UserConfigurations() {
    const { userNicknameRoute } = useParams<string>();
    const dispatch     = useDispatch<AppDispatch>();
    const navigate     = useNavigate();
    const currentUser  = useSelector((state: RootState) => state.session.currentUser);
    const authHeaders  = useSelector((state: RootState) => state.session.authHeaders);
    const loading      = useSelector((state: RootState) => state.sessionUserConfig.loading);
    const userMoreInfo = useSelector((state: RootState) => state.sessionUserConfig.userMoreInfo);

    const [sideBarOption, setSideBarOption] = useState('general');

    useEffect(() => {
        if (userNicknameRoute !== currentUser?.nickname) {
            const PRIVATE_ROUTES = REQUEST_REQUIREMENTS.handlePrivateRoutes({
                ROUTE_PARAMS: { userNickname: currentUser?.nickname },
            });
            navigate(PRIVATE_ROUTES.HOME);
            return;
        }
        if (authHeaders && currentUser?.nickname) {
            dispatch(getUserGenericConfig({ authHeaders, userNickname: currentUser.nickname }));
        }
    }, []);

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: BG_MAIN, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: BRAND_RED }} />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG_MAIN, py: { xs: 2, sm: 3, md: 4 } }}>
            <Container maxWidth='lg'>
                <Typography
                    fontWeight={700}
                    color={TEXT_PRIMARY}
                    sx={{ fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' } }}
                >
                    Settings
                </Typography>
                <Box sx={{ width: 36, height: 3, bgcolor: BRAND_RED, mt: 0.5, mb: { xs: 2, md: 3 } }} />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 3 }, alignItems: 'flex-start' }}>
                    {/* Navigation sidebar */}
                    <Box sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}>
                        <UserConfigSideBar
                            optionSelected={sideBarOption}
                            setOptionClicked={setSideBarOption}
                        />
                    </Box>

                    {/* Content panel */}
                    <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                        {sideBarOption === 'general' && (
                            <GeneralUserConfigForm userMoreInfo={userMoreInfo} />
                        )}
                        {sideBarOption === 'privacity' && (
                            <PrivacityUserConfigForm />
                        )}
                        {sideBarOption === 'accessibility' && (
                            <AccessibilityUserConfigForm />
                        )}
                        {sideBarOption === 'billing' && (
                            <BillingInfoForm />
                        )}
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
